import { canAccessBackoffice } from './auth';
import { getApiUrl } from './api';
import {
  CATALOG_WAITER_PRODUCTS_KEY,
  getCatalogStorageSession,
  readCatalogSessionJson,
  removeCatalogSessionValue,
  writeCatalogSessionJson,
} from './catalogSessionStorage';

export const WAITER_CATALOG_TIMEOUT_MESSAGE = 'O cardápio demorou demais para responder. Tente atualizar.';
export const WAITER_CATALOG_CACHE_FRESH_MS = 5 * 60 * 1000;
export const WAITER_CATALOG_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
export const OPERATIONAL_CATALOG_CACHE_FRESH_MS = WAITER_CATALOG_CACHE_FRESH_MS;
const WAITER_CATALOG_CACHE_SCHEMA = 2;
const ALTERNATIVE_IMAGE_CARRIERS = [
  'imagePath',
  'path',
  'url',
  'fileName',
  'logoUrl',
  'logosUrls',
  'imageUrls',
  'images',
  'image',
  'product',
  'productData',
  'rawProduct',
  'raw',
];

const isAllowedCachedImageUrl = (value) => {
  const source = String(value || '').trim();
  if (!source) return true;
  const internalPath = /^\/api\/(?:product-media\/[A-Za-z0-9_-]{32}|products\/order-display-image\/)/;
  if (internalPath.test(source)) return true;
  try {
    const pageOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://gestor.brutosdelivery.cloud';
    const url = new URL(source, pageOrigin);
    if (url.protocol !== 'https:' && !(url.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(url.hostname))) {
      return false;
    }
    if (
      url.origin === 'https://static-images.ifood.com.br'
      && ['/image/upload/', '/pratos/', '/logos/', '/menus/'].some((prefix) => url.pathname.startsWith(prefix))
    ) {
      return true;
    }
    const configuredApi = getApiUrl();
    const apiOrigin = configuredApi ? new URL(configuredApi, pageOrigin).origin : pageOrigin;
    return url.origin === apiOrigin && internalPath.test(url.pathname);
  } catch {
    return false;
  }
};

const hasAlternativeImageCarrier = (value) => (
  value
  && typeof value === 'object'
  && ALTERNATIVE_IMAGE_CARRIERS.some((key) => Object.prototype.hasOwnProperty.call(value, key))
);

const hasSafeCachedImageShape = (value) => (
  value
  && typeof value === 'object'
  && !hasAlternativeImageCarrier(value)
  && isAllowedCachedImageUrl(value.imageUrl)
);

const waiterProductsAreSafeToCache = (products) => {
  if (!Array.isArray(products)) return false;
  let serialized = '';
  try {
    serialized = JSON.stringify(products);
  } catch {
    return false;
  }
  if (
    serialized.length > 2 * 1024 * 1024
    || /data:image/i.test(serialized)
    || /"(?:payload|raw)"\s*:/i.test(serialized)
  ) return false;
  return products.every((product) => (
    hasSafeCachedImageShape(product)
    && (Array.isArray(product.itemOptionGroups) ? product.itemOptionGroups : []).every((group) => (
      (Array.isArray(group?.options) ? group.options : []).every((option) => (
        hasSafeCachedImageShape(option)
      ))
    ))
  ));
};

export const fetchWaiterOperationalCatalog = async (
  fetchCatalog,
  { timeoutMs = 20000, controller = null } = {}
) => {
  let timeoutId = null;
  let timedOut = false;
  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      timedOut = true;
      controller?.abort?.();
      reject(new Error(WAITER_CATALOG_TIMEOUT_MESSAGE));
    }, timeoutMs);
  });

  try {
    const operation = (async () => {
      const response = await fetchCatalog(controller?.signal);
      const responseEtag = response.headers?.get?.('etag') || '';
      const responseRevision = response.headers?.get?.('x-catalog-revision') || '';
      if (response.status === 304) {
        return {
          notModified: true,
          catalogRevision: responseRevision,
          etag: responseEtag,
        };
      }
      const data = await response.json().catch(() => null);
      if (!response.ok || data?.success === false) {
        throw new Error(data?.error || 'Falha ao carregar produtos.');
      }
      if (!data?.catalog || !Array.isArray(data.catalog.products)) {
        throw new Error('Resposta inválida do cardápio do Garçom.');
      }
      return {
        ...data.catalog,
        catalogRevision: data.catalog.catalogRevision
          ?? data.catalog.revision
          ?? data.catalogRevision
          ?? data.revision
          ?? responseRevision
          ?? '',
        etag: responseEtag || data.etag || data.catalog.etag || '',
      };
    })();
    try {
      return await Promise.race([operation, timeout]);
    } catch (error) {
      if (timedOut) throw new Error(WAITER_CATALOG_TIMEOUT_MESSAGE);
      throw error;
    }
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

export const readWaiterCatalogSnapshot = ({
  session = getCatalogStorageSession(),
  now = Date.now(),
} = {}) => {
  const snapshot = readCatalogSessionJson(CATALOG_WAITER_PRODUCTS_KEY, null, session);
  const cachedAt = Number(snapshot?.cachedAt);
  const age = Number(now) - cachedAt;
  if (
    snapshot?.schemaVersion !== WAITER_CATALOG_CACHE_SCHEMA
    || !waiterProductsAreSafeToCache(snapshot?.products)
    || !Number.isFinite(age)
    || age < 0
    || age > WAITER_CATALOG_CACHE_MAX_AGE_MS
  ) {
    removeCatalogSessionValue(CATALOG_WAITER_PRODUCTS_KEY, session);
    return null;
  }
  return {
    products: snapshot.products,
    cachedAt,
    isFresh: age <= WAITER_CATALOG_CACHE_FRESH_MS,
    catalogRevision: snapshot.catalogRevision ?? '',
    etag: String(snapshot.etag || ''),
  };
};

export const writeWaiterCatalogSnapshot = (
  products,
  {
    session = getCatalogStorageSession(),
    now = Date.now(),
    catalogRevision = '',
    etag = '',
  } = {}
) => {
  if (!waiterProductsAreSafeToCache(products)) return false;
  return writeCatalogSessionJson(CATALOG_WAITER_PRODUCTS_KEY, {
    schemaVersion: WAITER_CATALOG_CACHE_SCHEMA,
    cachedAt: Number(now),
    catalogRevision,
    etag: String(etag || ''),
    products,
  }, session);
};

export const canShowWaiterDeviceConfig = (user, deviceMode = false) => (
  !deviceMode && canAccessBackoffice(user)
);

export const findWaiterProductForSessionItem = (item = {}, products = []) => {
  const itemProductId = item.productDbId ?? item.productId;
  return products.find((product) => (
    itemProductId !== undefined
    && itemProductId !== null
    && Number(product.dbId ?? product.productId) === Number(itemProductId)
  ))
    || products.find((product) => product.itemId && product.itemId === item.itemId)
    || products.find((product) => product.itemExternalCode && product.itemExternalCode === item.externalCode)
    || products.find((product) => product.itemName && product.itemName === item.name)
    || null;
};

export const adaptWaiterDeviceCatalog = (catalog = {}) => (
  (Array.isArray(catalog.products) ? catalog.products : [])
    .filter((product) => product?.available !== false)
    .map((product) => ({
      dbId: product.productId,
      productId: product.productId,
      itemExternalCode: product.externalCode || '',
      itemId: product.externalCode || String(product.productId || ''),
      itemName: product.name || '',
      itemDescription: product.description || '',
      categoryName: product.categoryName || 'Sem categoria',
      imageUrl: product.imageUrl || '',
      unitPrice: Number(product.price || 0),
      itemPrice: { value: Number(product.price || 0) },
      isPizza: product.isPizza === true,
      showInBalcao: true,
      itemOptionGroups: (Array.isArray(product.optionGroups) ? product.optionGroups : []).map((group) => ({
        optionGroupId: group.groupId,
        groupId: group.groupId,
        name: group.name || '',
        optionGroupType: group.groupType || '',
        groupType: group.groupType || '',
        minQuantity: Number(group.minQuantity || 0),
        maxQuantity: Number(group.maxQuantity || 0),
        options: (Array.isArray(group.options) ? group.options : [])
          .filter((option) => option?.available !== false)
          .map((option) => ({
            optionId: option.optionId,
            externalCode: option.externalCode || '',
            name: option.name || '',
            description: option.description || '',
            imageUrl: option.imageUrl || '',
            price: { value: Number(option.price || 0) },
            contextualPrices: option.contextualPrices || {},
          })),
      })),
    }))
);

// Balcao and Garcom intentionally share the same compact, session-scoped catalog.
export const fetchOperationalCatalog = fetchWaiterOperationalCatalog;
export const readOperationalCatalogSnapshot = readWaiterCatalogSnapshot;
export const writeOperationalCatalogSnapshot = writeWaiterCatalogSnapshot;
export const adaptOperationalCatalog = adaptWaiterDeviceCatalog;

export const buildWaiterDeviceItemIntent = (product, quantity, observations, selectedOptions) => ({
  productId: product?.productId ?? product?.dbId,
  quantity: Math.max(1, Number(quantity || 1)),
  observations: String(observations || ''),
  options: (selectedOptions || []).map((option) => ({
    groupId: option.groupId,
    optionId: option.optionId,
    quantity: Math.max(1, Number(option.quantity || 1)),
  })),
});
